import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Stack,
  Chip,
  Rating,
  IconButton,
  Breadcrumbs,
  Link,
  Divider,
  Avatar,
  Badge,
  SwipeableDrawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Skeleton
} from '@mui/material';
import {
  ArrowBack,
  ArrowForward,
  WhatsApp,
  ShoppingCart,
  Favorite,
  Share,
  Store,
  Phone,
  Email,
  LocationOn,
  ChevronLeft,
  ChevronRight,
  Edit,
  Delete
} from '@mui/icons-material';
import { productService } from '../services/productService';
import reviewService from '../services/reviewService';
import { useAuth } from '../context/AuthContext';
import ImageCard from '../components/ImageCard';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [product, setProduct] = useState(null);
  const [products, setProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [reviewFormOpen, setReviewFormOpen] = useState(false);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [editingReview, setEditingReview] = useState(null);
  const [editReviewOpen, setEditReviewOpen] = useState(false);
  const [editReviewRating, setEditReviewRating] = useState(0);
  const [editReviewComment, setEditReviewComment] = useState('');

  // Scroll vers le haut quand on arrive sur la page
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  useEffect(() => {
    const fetchProductAndList = async () => {
      try {
        setLoading(true);
        // Récupérer le produit actuel
        const productData = await productService.getProduct(id);
        setProduct(productData);

        // Récupérer la liste des produits pour la navigation
        const productsData = await productService.getProducts();
        setProducts(productsData.products || productsData);

        setError(null);
      } catch (err) {
        console.error('Erreur lors du chargement du produit:', err);
        setError('Produit non trouvé');
      } finally {
        setLoading(false);
      }
    };

    const fetchReviews = async () => {
      try {
        setReviewsLoading(true);
        const reviewsData = await reviewService.getProductReviews(id);
        setReviews(reviewsData.reviews || []);
      } catch (err) {
        console.error('Erreur lors du chargement des avis:', err);
        setReviews([]);
      } finally {
        setReviewsLoading(false);
      }
    };

    if (id) {
      fetchProductAndList();
      fetchReviews();
    }
  }, [id]);

  // Raccourcis clavier pour la navigation
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.key === 'ArrowLeft' && getPreviousProduct()) {
        handlePreviousProduct();
      } else if (event.key === 'ArrowRight' && getNextProduct()) {
        handleNextProduct();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [products, id]);

  const handlePreviousImage = () => {
    if (product?.images?.length > 1) {
      setCurrentImageIndex((prev) =>
        prev === 0 ? product.images.length - 1 : prev - 1
      );
    }
  };

  const handleNextImage = () => {
    if (product?.images?.length > 1) {
      setCurrentImageIndex((prev) =>
        prev === product.images.length - 1 ? 0 : prev + 1
      );
    }
  };

  const handleWhatsAppClick = () => {
    // Si l'utilisateur a une boutique, utiliser le numéro de la boutique, sinon utiliser le numéro de l'utilisateur
    const contactPhone = product?.boutique
      ? (product.boutique.whatsapp || product.boutique.phone)
      : product?.user?.phone;

    if (contactPhone) {
      const phoneNumber = contactPhone;
      const message = `Bonjour, je suis intéressé par le produit "${product.name}"${product.boutique ? ` de ${product.boutique.name}` : ''}`;
      window.open(`https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`, '_blank');
    }
  };

  // Gérer la soumission d'un avis
  const handleSubmitReview = async () => {
    if (!reviewRating || reviewRating < 1) {
      alert('Veuillez sélectionner une note');
      return;
    }

    try {
      await reviewService.createProductReview(id, {
        rating: reviewRating,
        comment: reviewComment
      });

      // Recharger les avis et les données du produit
      const reviewsData = await reviewService.getProductReviews(id);
      setReviews(reviewsData.reviews || []);

      const productData = await productService.getProduct(id);
      setProduct(productData);

      // Réinitialiser le formulaire
      setReviewRating(0);
      setReviewComment('');
      setReviewFormOpen(false);

    } catch (err) {
      console.error('Erreur lors de l\'ajout de l\'avis:', err);
      alert(err.response?.data?.message || 'Erreur lors de l\'ajout de l\'avis');
    }
  };

  // Ouvrir le dialog d'édition
  const handleEditReview = (review) => {
    setEditingReview(review);
    setEditReviewRating(review.rating);
    setEditReviewComment(review.comment || '');
    setEditReviewOpen(true);
  };

  // Gérer la modification d'un avis
  const handleUpdateReview = async () => {
    if (!editReviewRating || editReviewRating < 1) {
      alert('Veuillez sélectionner une note');
      return;
    }

    try {
      await reviewService.updateReview(editingReview.id, {
        rating: editReviewRating,
        comment: editReviewComment
      });

      // Recharger les avis et les données du produit
      const reviewsData = await reviewService.getProductReviews(id);
      setReviews(reviewsData.reviews || []);

      const productData = await productService.getProduct(id);
      setProduct(productData);

      // Réinitialiser le formulaire
      setEditingReview(null);
      setEditReviewRating(0);
      setEditReviewComment('');
      setEditReviewOpen(false);

    } catch (err) {
      console.error('Erreur lors de la modification de l\'avis:', err);
      alert(err.response?.data?.message || 'Erreur lors de la modification de l\'avis');
    }
  };

  // Gérer la suppression d'un avis
  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer votre commentaire ?')) {
      return;
    }

    try {
      await reviewService.deleteReview(reviewId);

      // Recharger les avis et les données du produit
      const reviewsData = await reviewService.getProductReviews(id);
      setReviews(reviewsData.reviews || []);

      const productData = await productService.getProduct(id);
      setProduct(productData);

    } catch (err) {
      console.error('Erreur lors de la suppression de l\'avis:', err);
      alert(err.response?.data?.message || 'Erreur lors de la suppression de l\'avis');
    }
  };

  // Vérifier si l'utilisateur a déjà laissé un avis
  const hasUserReviewed = useMemo(() => {
    if (!isAuthenticated || !user || !reviews.length) {
      return false;
    }
    return reviews.some(review => review.reviewer?.id === user.id);
  }, [isAuthenticated, user, reviews]);

  // Navigation entre produits
  const getCurrentProductIndex = () => {
    return products.findIndex(p => p.id === id);
  };

  const getPreviousProduct = () => {
    const currentIndex = getCurrentProductIndex();
    if (currentIndex > 0) {
      return products[currentIndex - 1];
    }
    return null;
  };

  const getNextProduct = () => {
    const currentIndex = getCurrentProductIndex();
    if (currentIndex < products.length - 1) {
      return products[currentIndex + 1];
    }
    return null;
  };

  const handlePreviousProduct = () => {
    const previousProduct = getPreviousProduct();
    if (previousProduct) {
      navigate(`/products/${previousProduct.id}`);
    }
  };

  const handleNextProduct = () => {
    const nextProduct = getNextProduct();
    if (nextProduct) {
      navigate(`/products/${nextProduct.id}`);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {/* Breadcrumbs Skeleton */}
        <Skeleton variant="text" width={300} height={24} sx={{ mb: 3 }} />

        {/* Navigation Skeleton */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Skeleton variant="rectangular" width={100} height={36} />
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Skeleton variant="text" width={60} height={20} />
            <Skeleton variant="rectangular" width={80} height={32} />
            <Skeleton variant="rectangular" width={80} height={32} />
          </Box>
        </Box>

        <Grid container spacing={3}>
          {/* Informations produit Skeleton */}
          <Grid item xs={12} md={4.2}>
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ mb: 2 }}>
                  <Skeleton variant="text" width="80%" height={40} />
                  <Skeleton variant="rectangular" width={60} height={24} sx={{ mt: 1 }} />
                </Box>
                <Skeleton variant="rectangular" width={100} height={24} sx={{ mb: 2 }} />
                <Skeleton variant="text" width="60%" height={48} sx={{ mb: 2 }} />
                <Box sx={{ mb: 2 }}>
                  <Skeleton variant="text" width={120} height={24} />
                </Box>
                <Skeleton variant="text" width={150} height={20} sx={{ mb: 3 }} />
                <Box sx={{ mb: 2 }}>
                  <Skeleton variant="text" width={100} height={24} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                    <Skeleton variant="circular" width={40} height={40} />
                    <Box>
                      <Skeleton variant="text" width={120} height={20} />
                      <Skeleton variant="text" width={180} height={16} />
                    </Box>
                  </Box>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ mb: 2 }}>
                  <Skeleton variant="text" width={100} height={24} />
                  <Skeleton variant="rectangular" width="100%" height={150} sx={{ mt: 1, borderRadius: 1 }} />
                </Box>
                <Skeleton variant="rectangular" width="100%" height={40} />
              </CardContent>
            </Card>
          </Grid>

          {/* Images Skeleton */}
          <Grid item xs={12} md={7.8}>
            <Card>
              <Skeleton
                variant="rectangular"
                width="100%"
                height={{ xs: 300, md: 400 }}
                sx={{ borderRadius: '4px 4px 0 0' }}
              />
              <Box sx={{ p: 2, display: 'flex', gap: 1, overflowX: 'auto' }}>
                {[...Array(4)].map((_, index) => (
                  <Skeleton
                    key={index}
                    variant="rectangular"
                    width={60}
                    height={60}
                    sx={{ borderRadius: 1, flexShrink: 0 }}
                  />
                ))}
              </Box>
            </Card>
          </Grid>
        </Grid>

        {/* Avis Section Skeleton */}
        <Card sx={{ mt: 4 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Skeleton variant="text" width={150} height={32} />
              <Skeleton variant="rectangular" width={140} height={36} />
            </Box>
            {[...Array(2)].map((_, index) => (
              <Box key={index} sx={{ mb: 3, pb: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Skeleton variant="circular" width={40} height={40} />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton variant="text" width={150} height={20} />
                    <Skeleton variant="text" width={100} height={20} />
                  </Box>
                  <Skeleton variant="text" width={80} height={16} />
                </Box>
                <Skeleton variant="text" width="90%" height={16} sx={{ ml: 7 }} />
                <Skeleton variant="text" width="70%" height={16} sx={{ ml: 7 }} />
              </Box>
            ))}
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (error || !product) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <Typography variant="h6" color="error">
            {error || 'Produit non trouvé'}
          </Typography>
        </Box>
      </Container>
    );
  }

  const hasMultipleImages = product.images && product.images.length > 1;
  const currentImage = product.images?.[currentImageIndex] || null;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Navigation Breadcrumb */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          color="inherit"
          href="/"
          onClick={(e) => { e.preventDefault(); navigate('/'); }}
          sx={{ cursor: 'pointer' }}
        >
          Accueil
        </Link>
        <Link
          color="inherit"
          href="/products"
          onClick={(e) => { e.preventDefault(); navigate('/products'); }}
          sx={{ cursor: 'pointer' }}
        >
          Produits
        </Link>
        <Typography color="text.primary">{product.name}</Typography>
      </Breadcrumbs>

      {/* Navigation Produits */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(-1)}
          variant="outlined"
        >
          Retour
        </Button>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="body2" color="text.secondary">
            {getCurrentProductIndex() + 1} / {products.length}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              startIcon={<ArrowBack />}
              variant="outlined"
              size="small"
              onClick={handlePreviousProduct}
              disabled={!getPreviousProduct()}
              title={getPreviousProduct() ? `Préc: ${getPreviousProduct().name}` : 'Premier produit'}
            >
              Préc
            </Button>
            <Button
              endIcon={<ArrowForward />}
              variant="outlined"
              size="small"
              onClick={handleNextProduct}
              disabled={!getNextProduct()}
              title={getNextProduct() ? `Suiv: ${getNextProduct().name}` : 'Dernier produit'}
            >
              Suiv
            </Button>
          </Stack>
        </Stack>
      </Box>

      <Grid container spacing={3}>
        {/* 📋 Informations produit - 35% sur desktop, 100% sur mobile */}
        <Grid item xs={12} md={4.2} sx={{
          maxWidth: { md: '400px' },
          width: '100%'
        }}>
          <Card sx={{
            height: '100%',
            maxHeight: { md: '600px' },
            width: '100%'
          }}>
            <CardContent sx={{
              p: 3,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}>
              {/* Titre */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {product.name}
                </Typography>
                {product.isVip && (
                  <Chip
                    label="VIP"
                    color="warning"
                    size="medium"
                    sx={{ fontWeight: 600 }}
                  />
                )}
              </Box>

              {product.category && (
                <Chip
                  label={product.category}
                  color="primary"
                  variant="outlined"
                  sx={{
                    mb: 2,
                    width: 'fit-content',
                    maxWidth: '100%',
                    '& .MuiChip-label': {
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }
                  }}
                />
              )}

              {/* Prix */}
              <Typography variant="h3" color="primary.main" sx={{ fontWeight: 700, mb: 1 }}>
                {parseFloat(product.price || 0).toLocaleString('fr-FR', { useGrouping: true, minimumFractionDigits: 0, maximumFractionDigits: 0 })} FCFA
              </Typography>

              {/* Note moyenne */}
              <Box sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Rating
                    value={product.averageRating || 0}
                    precision={0.1}
                    size="small"
                    readOnly
                  />
                  <Typography variant="body2" color="text.secondary">
                    ({product.totalReviews || 0} avis)
                  </Typography>
                </Stack>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}
                style={{
                  // textDecoration: product.isAvailable ? "none" : "line-through",
                  textDecoration: product.isAvailable ? "none" : "none",
                  color: product.isAvailable ? "green" : "red",
                }}
              >
                {product.isAvailable ? (
                  <>
                   Stock : {product.stockQuantity || 0}
                  </>
                ) : (
                  <>
                    Stock : 0
                  </>
                )}
              </Typography>


              {/* Boutique */}
              {product.boutique && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="h6" gutterBottom>
                    Vendu par
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      cursor: 'pointer',
                      p: 1,
                      borderRadius: 1,
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                    onClick={() => navigate(`/boutiques/${product.boutique?.id}`)}
                  >
                    <Avatar
                      src={product.boutique?.logo || null}
                      sx={{ bgcolor: 'primary.main' }}
                    >
                      {!product.boutique?.logo && <Store />}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {product.boutique.name}
                      </Typography>
                      {product.boutique.address && (
                        <Typography variant="body2" color="text.secondary">
                          <LocationOn sx={{ fontSize: 13, mr: 0.5, verticalAlign: 'middle' }} />
                          {product.boutique.address}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              )}

              <Divider sx={{ my: 1 }} />

              {/* Description */}
              <Box sx={{
                mb: 3,
                flex: 1,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column'
              }}>
                <Typography variant="h6" gutterBottom>
                  Description
                </Typography>
                <Box sx={{
                  flex: 1,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  p: 1,
                  bgcolor: 'grey.50',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'grey.200',
                  '&::-webkit-scrollbar': { width: '6px' },
                  '&::-webkit-scrollbar-track': { background: '#f1f1f1', borderRadius: '3px' },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#c1c1c1',
                    borderRadius: '3px',
                    '&:hover': { background: '#a8a8a8' },
                  },
                }}>
                  <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.9 }}>
                    {product.description || 'Aucune description disponible'}
                  </Typography>
                </Box>
              </Box>

              {/* Actions - Section fixe en bas */}
              <Box sx={{ flexShrink: 0 }}>
                <Stack direction="row" spacing={2}>
                  {(() => {
                    // Si l'utilisateur a une boutique, utiliser le numéro de la boutique, sinon utiliser le numéro de l'utilisateur
                    const contactPhone = product.boutique
                      ? (product.boutique.whatsapp || product.boutique.phone)
                      : product.user?.phone;
                    return contactPhone && (
                      <Button
                        variant="contained"
                        size="large"
                        startIcon={<WhatsApp />}
                        onClick={handleWhatsAppClick}
                        sx={{
                          bgcolor: '#25D366',
                          '&:hover': { bgcolor: '#128C7E' }
                        }}
                      >
                        WhatsApp
                      </Button>
                    );
                  })()}
                  {/* <IconButton color="primary">
                    <Favorite />
                  </IconButton>
                  <IconButton color="primary">
                    <Share />
                  </IconButton> */}
                </Stack>

                {/* <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2 }}>
                  <IconButton color="primary">
                    <Favorite />
                  </IconButton>
                  <IconButton color="primary">
                    <Share />
                  </IconButton>
                </Stack> */}
              </Box>
            </CardContent>
          </Card>
        </Grid>


        {/* 🖼️ Images - 65% sur desktop, 100% sur mobile */}
        <Grid item xs={12} md={7.8} sx={{
          maxWidth: { md: '725px' },
          width: '100%'
        }}>
          <Card sx={{
            position: 'relative',
            width: '100%',
            height: '100%'
          }}>
            <Box sx={{
              position: 'relative',
              width: '100%',
              height: { xs: 300, md: 400 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <ImageCard
                src={currentImage}
                alt={product.name}
                height="100%"
                sx={{
                  borderRadius: 1,
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain'
                }}
              />

              {/* Badge VIP */}
              {/* {product.isVip && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    bgcolor: 'warning.main',
                    color: 'white',
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 1,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    zIndex: 1
                  }}
                >
                  VIP
                </Box>
              )} */}

              {/* Badge En rupture */}
              {(!product.isAvailable || product.stockQuantity === 0) && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    bgcolor: 'error.main',
                    color: 'white',
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 1,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    zIndex: 1
                  }}
                >
                  En rupture
                </Box>
              )}

              {/* Navigation */}
              {hasMultipleImages && (
                <>
                  <IconButton
                    onClick={handlePreviousImage}
                    sx={{
                      position: 'absolute',
                      left: 8,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      bgcolor: 'rgba(255,255,255,0.9)',
                      '&:hover': { bgcolor: 'white' }
                    }}
                  >
                    <ChevronLeft />
                  </IconButton>
                  <IconButton
                    onClick={handleNextImage}
                    sx={{
                      position: 'absolute',
                      right: 8,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      bgcolor: 'rgba(255,255,255,0.9)',
                      '&:hover': { bgcolor: 'white' }
                    }}
                  >
                    <ChevronRight />
                  </IconButton>
                </>
              )}

              {/* Indicateur */}
              {hasMultipleImages && (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 8,
                    right: 8,
                    bgcolor: 'rgba(0,0,0,0.7)',
                    color: 'white',
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 1,
                    fontSize: '0.75rem',
                    fontWeight: 500
                  }}
                >
                  {currentImageIndex + 1} / {product.images.length}
                </Box>
              )}
            </Box>

            {/* Miniatures */}
            {hasMultipleImages && (
              <Box
                sx={{
                  p: 2,
                  display: 'flex',
                  gap: 1,
                  overflowX: 'auto',
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'grey.50',
                  width: '100%'
                }}
              >
                {product.images.map((image, index) => (
                  <Box
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    sx={{
                      minWidth: 60,
                      height: 60,
                      borderRadius: 1,
                      overflow: 'hidden',
                      cursor: 'pointer',
                      border: currentImageIndex === index
                        ? '2px solid #1976d2'
                        : '2px solid transparent',
                      opacity: currentImageIndex === index ? 1 : 0.7,
                      '&:hover': { opacity: 1 },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <ImageCard
                      src={image}
                      alt={`${product.name} ${index + 1}`}
                      height={60}
                      sx={{ width: '100%', height: '100%' }}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Card>
        </Grid>


      </Grid>

      {/* Section Avis */}
      <Card sx={{ mt: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5">
              Avis ({product?.totalReviews || 0})
            </Typography>
            {isAuthenticated && (
              <Button
                variant="contained"
                onClick={() => setReviewFormOpen(true)}
                disabled={hasUserReviewed}
                title={hasUserReviewed ? 'Vous avez déjà laissé un avis' : 'Laisser un avis'}
              >
                Laisser un avis
              </Button>
            )}
          </Box>

          {reviewsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : reviews.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              Aucun avis pour le moment
            </Typography>
          ) : (
            <Stack spacing={3}>
              {reviews.map((review) => (
                <Box key={review.id} sx={{ borderBottom: 1, borderColor: 'divider', pb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                    <Avatar src={review.reviewer?.profilePhoto}>
                      {review.reviewer?.name?.charAt(0)}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {review.reviewer?.name || 'Utilisateur anonyme'}
                      </Typography>
                      <Rating value={review.rating} readOnly size="small" />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(review.createdAt).toLocaleDateString('fr-FR')}
                      </Typography>
                      {isAuthenticated && user && review.reviewer?.id === user.id && (
                        <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleEditReview(review)}
                            color="primary"
                            title="Modifier le commentaire"
                            sx={{ fontSize: '12px' }}
                          >
                            {/* <Edit fontSize="small" /> */}
                            Editer
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteReview(review.id)}
                            color="error"
                            title="Supprimer le commentaire"
                            sx={{ fontSize: '12px' }}
                          >
                            {/* <Delete fontSize="small" /> */}
                            Supprimer
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                  </Box>
                  {review.comment && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 7 }}>
                      {review.isActive === false ? 'En attente d\'approbation...' : review.comment}
                    </Typography>
                  )}
                  {!review.comment && review.isActive === false && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 7, fontStyle: 'italic' }}>
                      En attente d'approbation...
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* Dialog pour laisser un avis */}
      <Dialog open={reviewFormOpen} onClose={() => setReviewFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Laisser un avis</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Note
            </Typography>
            <Rating
              value={reviewRating}
              onChange={(event, newValue) => {
                setReviewRating(newValue);
              }}
              size="large"
            />
          </Box>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Commentaire (optionnel)"
            value={reviewComment}
            onChange={(e) => setReviewComment(e.target.value)}
            sx={{ mt: 3 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setReviewFormOpen(false);
            setReviewRating(0);
            setReviewComment('');
          }}>
            Annuler
          </Button>
          <Button onClick={handleSubmitReview} variant="contained" disabled={reviewRating < 1}>
            Publier
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog pour modifier un avis */}
      <Dialog open={editReviewOpen} onClose={() => setEditReviewOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Modifier votre commentaire</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Note
            </Typography>
            <Rating
              value={editReviewRating}
              onChange={(event, newValue) => {
                setEditReviewRating(newValue);
              }}
              size="large"
            />
          </Box>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Commentaire (optionnel)"
            value={editReviewComment}
            onChange={(e) => setEditReviewComment(e.target.value)}
            sx={{ mt: 3 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEditReviewOpen(false);
            setEditingReview(null);
            setEditReviewRating(0);
            setEditReviewComment('');
          }}>
            Annuler
          </Button>
          <Button onClick={handleUpdateReview} variant="contained" disabled={editReviewRating < 1}>
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

    </Container>
  );
};

export default ProductDetail;